import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Editor, { BtnBold, BtnBulletList, BtnItalic, BtnNumberedList, Toolbar, createButton } from 'react-simple-wysiwyg';
import { askText } from './AppDialogs';
import { linkHref } from './RichText';
import './RichTextField.css';

// Link button for both rich-text editors (this one and
// MergeFieldRichTextField.js) — the library's own BtnLink asks for the
// address with the browser's prompt box, which ui-architecture.md's
// "Confirm Dialogs" rules out. The dialog holds focus (its focus trap is
// still mounted when askText resolves), so execCommand('createLink') would
// act on the dialog's input; the link is built at the saved Range instead,
// same mechanics as insertMention below. Selected text becomes the link
// text; with nothing selected, the address as typed does. A link already
// under the caret is removed instead, same as BtnLink.
export const BtnLinkDialog = createButton('Link', '🔗', async ({ $el, $selection }) => {
  if ($selection?.nodeName === 'A') { document.execCommand('unlink'); return; }
  const sel = window.getSelection();
  if (!$el || !sel || sel.rangeCount === 0 || !$el.contains(sel.anchorNode)) return;
  const range = sel.getRangeAt(0).cloneRange();
  const url = await askText('Web address:', { title: 'Add Link', confirmLabel: 'Add Link' });
  if (!url || !url.trim()) return;

  const link = document.createElement('a');
  link.setAttribute('href', linkHref(url));
  if (range.collapsed) link.textContent = url.trim();
  else link.appendChild(range.extractContents());
  range.insertNode(link);

  $el.dispatchEvent(new Event('input', { bubbles: true }));
});

// Reads the @-mentions back out of saved notes HTML (a plain <a
// data-entity-id> — see insertMention below) so a consumer can fold the
// mentioned People into whatever else associates them with the record
// (e.g. LogActivityDrawer.js folds them into the Activity's own
// "Associated With" list, which is what makes the mention show up on that
// Person's own Activity feed — see models/activityContact.js). Plain
// DOMParser read, not a regex, so attribute quoting/ordering can't matter.
export function extractMentionedEntityIds(html) {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const ids = [...doc.querySelectorAll('a[data-entity-id]')].map((el) => el.getAttribute('data-entity-id'));
  return [...new Set(ids)];
}

// Looks backward from the caret for an unfinished "@query" — only within
// the caret's own text node, which covers the normal case of typing
// straight through. A query broken by whitespace, or one that crosses a
// formatting-node boundary (e.g. right after a **bold** run), just closes
// the dropdown rather than trying to reconstruct text across nodes.
function findMentionQuery() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;
  const textBefore = node.textContent.slice(0, range.startOffset);
  const at = textBefore.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(textBefore[at - 1])) return null;
  const query = textBefore.slice(at + 1);
  if (!query || /\s/.test(query) || query.length > 40) return null;
  return { query, atOffset: at, caretOffset: range.startOffset, textNode: node };
}

const mentionListStyle = {
  position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 260, margin: '4px 0 0', padding: 4,
  listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
};

// Shared basic-formatting field (Bold, Italic, Link and lists, plus @-mention
// autocomplete for Individuals — per ui-architecture.md's "Rich Text Notes
// Field" section). value/onChange carry an HTML string, same controlled-
// input shape as a plain textarea, so a consumer swaps one for the other
// with no other change. Never renders raw HTML unsanitized itself; pair
// with RichText.js's sanitizeHtml/RichText wherever this value gets
// displayed elsewhere (a list row, etc.) — this library edits HTML but
// deliberately doesn't sanitize it (see its own README), so the sanitize
// step always lives at the render/display end.
function RichTextField({ value, onChange, placeholder, disabled }) {
  const editorRef = useRef(null);
  const searchTimerRef = useRef(null);
  const [mention, setMention] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const closeMention = () => {
    clearTimeout(searchTimerRef.current);
    setMention(null);
    setMentionResults([]);
  };

  // Individuals only (a @mention names a person, never a Company), same
  // quick-search the "Associated With" picker already uses.
  const runSearch = (query) => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      axios.get('/api/commercial-entities/quick-search', { params: { q: query, type: 'individual' } })
        .then((res) => { setMentionResults(res.data.data || []); setMentionActiveIndex(0); })
        .catch(() => setMentionResults([]));
    }, 200);
  };

  const handleKeyUp = () => {
    const info = findMentionQuery();
    if (info) {
      setMention(info);
      runSearch(info.query);
    } else {
      closeMention();
    }
  };

  // Replaces the "@query" text with a plain link to the Person's own
  // detail page — full name, no "@" (reads naturally in a sentence) — and
  // fires a native `input` event afterwards so react-simple-wysiwyg's own
  // change-detection (wired to onInput, see its ContentEditable) picks up
  // the DOM mutation the same as it would a keystroke.
  const insertMention = (person) => {
    const info = findMentionQuery();
    const el = editorRef.current;
    if (!info || !el) { closeMention(); return; }

    const range = document.createRange();
    range.setStart(info.textNode, info.atOffset);
    range.setEnd(info.textNode, info.caretOffset);
    range.deleteContents();

    const link = document.createElement('a');
    link.setAttribute('href', `/commercial-entities/${person.id}`);
    link.setAttribute('data-entity-id', String(person.id));
    link.textContent = person.name;
    const trailingSpace = document.createTextNode(' ');
    const frag = document.createDocumentFragment();
    frag.appendChild(link);
    frag.appendChild(trailingSpace);
    range.insertNode(frag);

    const after = document.createRange();
    after.setStartAfter(trailingSpace);
    after.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(after);

    el.dispatchEvent(new Event('input', { bubbles: true }));
    closeMention();
  };

  const handleKeyDown = (e) => {
    if (!mention || !mentionResults.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionActiveIndex((i) => (i + 1) % mentionResults.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionActiveIndex((i) => (i - 1 + mentionResults.length) % mentionResults.length); }
    else if (e.key === 'Enter') { e.preventDefault(); insertMention(mentionResults[mentionActiveIndex]); }
    else if (e.key === 'Escape') { e.preventDefault(); closeMention(); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Editor
        ref={editorRef}
        containerProps={{ className: 'rich-text-field' }}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onBlur={closeMention}
        placeholder={placeholder}
        disabled={disabled}
      >
        <Toolbar>
          <BtnBold />
          <BtnItalic />
          <BtnLinkDialog />
          <BtnBulletList />
          <BtnNumberedList />
        </Toolbar>
      </Editor>
      {mention && mentionResults.length > 0 && (
        <ul style={mentionListStyle}>
          {mentionResults.map((person, i) => (
            <li
              key={person.id}
              onMouseDown={(e) => { e.preventDefault(); insertMention(person); }}
              style={{ padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 13, background: i === mentionActiveIndex ? '#eef3fb' : 'transparent' }}
            >
              {person.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default RichTextField;
